import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
  type ColumnSizingState,
  type OnChangeFn,
} from '@tanstack/react-table';
import { ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react';
import Loading from './Loading';
import type { PaginationMeta } from '../types';

export interface DataTableProps<T> {
  /** Column definitions in tanstack format. */
  columns: ColumnDef<T, any>[];
  /** Current page of rows. */
  data: T[];
  /** Pagination metadata from the API. Pass null while loading. */
  meta: PaginationMeta | null;
  /** Whether the data is loading. */
  isLoading?: boolean;
  /** Current page (1-indexed). */
  page: number;
  /** Setter for page changes (prev/next buttons call this). */
  onPageChange: (page: number) => void;
  /** Sort state (server-side). Empty array = no sort. */
  sorting: SortingState;
  /** Setter for sort state. */
  onSortingChange: OnChangeFn<SortingState>;
  /** Column resize state (column id -> pixel width). */
  columnSizing: ColumnSizingState;
  /** Setter for resize state. */
  onColumnSizingChange: OnChangeFn<ColumnSizingState>;
  /** Empty-state message when data is empty. */
  emptyMessage?: string;
  /**
   * When true, the table fits the container width instead of scrolling
   * horizontally. Column widths still come from the column defs (and stay
   * resizable), but cells with `overflow-hidden text-ellipsis whitespace-nowrap`
   * truncate when total content exceeds the available space.
   */
  fitToWidth?: boolean;
}

/**
 * Server-side data table built on @tanstack/react-table.
 *
 * - Click a column header to sort (single column).
 * - Shift-click to add additional sort columns (multi-sort).
 * - Drag the right edge of any header to resize the column.
 * - Pagination is server-side; tanstack just renders what we hand it.
 */
export function DataTable<T>({
  columns,
  data,
  meta,
  isLoading = false,
  page,
  onPageChange,
  sorting,
  onSortingChange,
  columnSizing,
  onColumnSizingChange,
  emptyMessage = 'No results',
  fitToWidth = false,
}: DataTableProps<T>) {
  const table = useReactTable<T>({
    data,
    columns,
    state: { sorting, columnSizing },
    onSortingChange,
    onColumnSizingChange,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    enableMultiSort: true,
    enableSortingRemoval: true,
    isMultiSortEvent: (e) => (e as React.MouseEvent).shiftKey,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
  });

  return (
    <div className="card overflow-hidden p-0">
      {isLoading ? (
        <div className="p-12">
          <Loading />
        </div>
      ) : (() => {
        // In fitToWidth mode columns either have a fixed pixel width (when
        // the column def sets meta.noScale) or share the remaining space
        // proportionally via CSS calc().
        //
        // Math:
        //   fixedTotal = sum of getSize() over noScale columns
        //   flexTotal  = sum of getSize() over the rest
        //   noScale column width  = `${getSize()}px`
        //   flexible column width = `calc((100% - <fixedTotal>px) * <fraction>)`
        //     where fraction = thisColumnSize / flexTotal
        //
        // This pins the noScale columns to their pixel widths and lets the
        // others squeeze to fit whatever's left. Resize handles still mutate
        // the underlying pixel sizes via tanstack, so the calculations stay
        // in sync as the user drags.
        const allCols = table.getAllLeafColumns();
        const isNoScale = (col: typeof allCols[number]) =>
          (col.columnDef.meta as { noScale?: boolean } | undefined)?.noScale === true;
        const fixedTotal = allCols
          .filter(isNoScale)
          .reduce((sum, c) => sum + c.getSize(), 0);
        const flexTotal = allCols
          .filter((c) => !isNoScale(c))
          .reduce((sum, c) => sum + c.getSize(), 0);

        const widthFor = (col: typeof allCols[number]): string | number => {
          if (!fitToWidth) return col.getSize();
          if (isNoScale(col)) return `${col.getSize()}px`;
          if (flexTotal === 0) return `${col.getSize()}px`;
          const fraction = col.getSize() / flexTotal;
          return `calc((100% - ${fixedTotal}px) * ${fraction})`;
        };

        return (
        <div className={fitToWidth ? '' : 'overflow-x-auto'}>
          <table
            className="min-w-full divide-y divide-gray-200"
            style={fitToWidth ? { width: '100%', tableLayout: 'fixed' } : { width: table.getCenterTotalSize() }}
          >
            <thead className="bg-gray-50">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const sortDir = header.column.getIsSorted();
                    const sortIndex = header.column.getSortIndex();
                    const isMultiSort = sorting.length > 1 && sortIndex >= 0;

                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        style={{ width: widthFor(header.column) }}
                        className="relative px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        <div
                          className={`flex items-center gap-1 ${canSort ? 'cursor-pointer select-none hover:text-gray-700' : ''}`}
                          onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                          title={canSort ? 'Click to sort. Shift-click to add to multi-sort.' : undefined}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort && (
                            <span className="inline-flex items-center text-gray-400">
                              {sortDir === 'asc' ? (
                                <ArrowUp className="h-3 w-3 text-gray-700" />
                              ) : sortDir === 'desc' ? (
                                <ArrowDown className="h-3 w-3 text-gray-700" />
                              ) : (
                                <ChevronsUpDown className="h-3 w-3" />
                              )}
                              {isMultiSort && (
                                <span className="ml-0.5 text-[10px] font-bold text-gray-600">
                                  {sortIndex + 1}
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                        {header.column.getCanResize() && (
                          <div
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            onClick={(e) => e.stopPropagation()}
                            className={`absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none bg-transparent hover:bg-blue-400 ${header.column.getIsResizing() ? 'bg-blue-500' : ''}`}
                          />
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-4 py-8 text-center text-sm text-gray-500"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{ width: widthFor(cell.column) }}
                        className="px-4 py-3 text-sm overflow-hidden text-ellipsis whitespace-nowrap"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        );
      })()}

      {meta && meta.pages > 0 && (
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm">
          <div className="text-gray-600">
            Page {meta.page} of {meta.pages} ({meta.count} total)
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={meta.page <= 1}
              onClick={() => onPageChange(page - 1)}
              className="btn btn-secondary py-1 px-3 disabled:opacity-50"
            >
              Prev
            </button>
            <button
              type="button"
              disabled={meta.page >= meta.pages}
              onClick={() => onPageChange(page + 1)}
              className="btn btn-secondary py-1 px-3 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
